import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, CheckCircle, Loader, ArrowLeft, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const VCE_SUBJECTS = [
  "English", "EAL", "Mathematical Methods", "Specialist Maths",
  "General Maths", "Further Maths", "Biology", "Chemistry",
  "Physics", "Accounting", "Economics", "Business Management",
  "Psychology", "Legal Studies", "IELTS", "Other"
];

const ManualTutorApprovalPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    subjects: [],
    profileBio: '',
    yearsExperience: '',
    tutoringMode: 'Both',
    hourlyRate: '',
    qualifications: '',
    availability: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubjectToggle = (subject) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.includes(subject)
        ? prev.subjects.filter(s => s !== subject)
        : [...prev.subjects, subject]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.email || formData.subjects.length === 0 || !formData.profileBio) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create tutor profile directly (bypassing application flow)
      const tutorProfile = {
        id: `tutor_${Date.now()}`,
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        subjects: formData.subjects,
        profileBio: formData.profileBio,
        yearsExperience: parseInt(formData.yearsExperience) || 0,
        tutoringMode: formData.tutoringMode,
        hourlyRate: parseFloat(formData.hourlyRate) || 0,
        qualifications: formData.qualifications,
        availability: formData.availability,
        isActive: true,
        isPubliclyVisible: true,
        availableForNewStudents: true,
        payoutPercentage: 60,
        totalLessons: 0,
        activeStudents: 0,
        totalEarnings: 0,
        createdAt: new Date().toISOString()
      };

      const response = await fetch(`${BACKEND_URL}/api/admin/manual-tutor-approval`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Send auth cookies
        body: JSON.stringify(tutorProfile)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to add tutor');
      }

      toast.success(`${formData.fullName} has been added to the Tutor Directory!`);
      
      // Reset form
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        subjects: [],
        profileBio: '',
        yearsExperience: '',
        tutoringMode: 'Both',
        hourlyRate: '',
        qualifications: '',
        availability: ''
      });

    } catch (error) {
      toast.error(error.message || 'Failed to add tutor. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <UserPlus className="w-6 h-6 text-blue-600" />
              Manual Tutor Approval
            </h1>
            <div className="flex gap-3">
              <Button onClick={() => navigate('/admin/dashboard')} variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <Button onClick={() => navigate('/')} variant="outline">
                <Home className="w-4 h-4 mr-2" />
                Home
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Card className="p-8 bg-white shadow-lg">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Add Approved Tutor to Directory</h2>
            <p className="text-gray-600">
              Use this form to manually add tutors who have emailed their CV to info@winhousetutors.com
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</Label>
                  <Input
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="John Smith"
                    required
                  />
                </div>
                <div>
                  <Label className="block text-sm font-medium text-gray-700 mb-2">Email *</Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                    required
                  />
                </div>
                <div>
                  <Label className="block text-sm font-medium text-gray-700 mb-2">Phone</Label>
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="0412 345 678"
                  />
                </div>
                <div>
                  <Label className="block text-sm font-medium text-gray-700 mb-2">Years of Experience</Label>
                  <Input
                    name="yearsExperience"
                    type="number"
                    value={formData.yearsExperience}
                    onChange={handleInputChange}
                    placeholder="3"
                  />
                </div>
              </div>
            </div>

            {/* Teaching Details */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Teaching Details</h3>
              
              <div className="mb-6">
                <Label className="block text-sm font-medium text-gray-700 mb-3">Subjects * (Select all that apply)</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {VCE_SUBJECTS.map(subject => (
                    <button
                      key={subject}
                      type="button"
                      onClick={() => handleSubjectToggle(subject)}
                      className={`px-4 py-2 rounded-lg border-2 transition-all text-sm ${
                        formData.subjects.includes(subject)
                          ? 'border-blue-600 bg-blue-600 text-white'
                          : 'border-gray-300 hover:border-blue-400'
                      }`}
                    >
                      {subject}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label className="block text-sm font-medium text-gray-700 mb-2">Tutoring Mode</Label>
                  <select
                    name="tutoringMode"
                    value={formData.tutoringMode}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="Online Only">Online Only</option>
                    <option value="In-Person Only">In-Person Only</option>
                    <option value="Both">Both</option>
                  </select>
                </div>
                <div>
                  <Label className="block text-sm font-medium text-gray-700 mb-2">Hourly Rate (AUD)</Label>
                  <Input
                    name="hourlyRate"
                    type="number"
                    value={formData.hourlyRate}
                    onChange={handleInputChange}
                    placeholder="50"
                  />
                </div>
              </div>

              <div className="mb-6">
                <Label className="block text-sm font-medium text-gray-700 mb-2">Profile Bio / About * (Shown on directory)</Label>
                <Textarea
                  name="profileBio"
                  value={formData.profileBio}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Experienced VCE tutor specializing in Mathematics and Science..."
                  required
                />
              </div>

              <div className="mb-6">
                <Label className="block text-sm font-medium text-gray-700 mb-2">Qualifications (Degree, Certifications)</Label>
                <Textarea
                  name="qualifications"
                  value={formData.qualifications}
                  onChange={handleInputChange}
                  rows={2}
                  placeholder="Bachelor of Science (University of Melbourne), ATAR 98.5"
                />
              </div>

              <div>
                <Label className="block text-sm font-medium text-gray-700 mb-2">Availability</Label>
                <Textarea
                  name="availability"
                  value={formData.availability}
                  onChange={handleInputChange}
                  rows={2}
                  placeholder="Weekday evenings and weekends"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-between pt-6 border-t">
              <p className="text-sm text-gray-600">
                * Required fields
              </p>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Adding Tutor...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Add to Directory
                  </>
                )}
              </Button>
            </div>
          </form>
        </Card>

        {/* Instructions */}
        <Card className="mt-6 p-6 bg-blue-50 border-blue-200">
          <h3 className="font-semibold text-blue-900 mb-3">📋 Workflow Instructions</h3>
          <ol className="space-y-2 text-sm text-blue-800">
            <li>1. Applicants email their CV to <strong>info@winhousetutors.com</strong></li>
            <li>2. Review their CV and application form submission in Admin Dashboard</li>
            <li>3. If approved, use this form to manually add them to the "Find a Tutor" page</li>
            <li>4. The tutor will immediately appear in the public directory</li>
          </ol>
        </Card>
      </div>
    </div>
  );
};

export default ManualTutorApprovalPage;
